import { webapi } from '@spot-fe/utils';
import { fetchWithCache } from './storekit';
import { ConstantCacheKeys } from './constant_cache_keys';

export interface Header {
  totalPrizePool: string;
  averageBenefits: string;
  participants: number;
  projects: number;
}

// 头图
export const getHeader = () => {
  const fn = function () {
    return webapi.get<Header>(`/spot/api/deposit-activity/v1/project/home`);
  };
  return fetchWithCache(fn, ConstantCacheKeys.HOME);
};

export interface ProjectListParams {
  pageNo: number;
  pageSize: number;
}

export interface Project {
  code: string;
  totalPrizePool: string;
  token: string;
  tokenFullName: string;
  // depositTokenList: string[];
  icon: string;
  participants: number;
  averageBenefits: string;
  depositStart: number;
  depositEnd: number;
  publishTime: number;
  applyStart: number;
  applyEnd: number;
  systemTime: number;
  prizeToken: string;
  taskType: number;
}

interface ProjectList<T = Project> {
  current: number;
  pageSize: number;
  total: number;
  list: T[];
}

// 列表
export const getProjectList = (params: ProjectListParams) => {
  return webapi.get<ProjectList>(
    '/spot/api/deposit-activity/v1/project/projectList',
    params
  );
};

export interface OnGoingProject extends Project {
  taskType: TaskType; // 任务类型( 1.入金任务 2 交易任务 3 入金 + 交易)
  prizeType: PrizeType; // 奖池模式 1.新用户2.老用户3.新老用户
  tokenList: string[]; // 入金&交易配置所有条件币种
  isJoin: number; // 用户报名状态 0:未报名 1:已报名
  taskStatus: TaskStatus; // 用户任务完成状态 0:未完成 1:已完成
  prizePool: string; // Referral奖池金额
  awardToken: string; // Referral奖池币种
}

export enum TaskStatus {
  Ongoing = 0,
  End = 1,
}

export const getOnGoingProjectList = (params?: ProjectListParams) => {
  const fn = function () {
    return webapi.get<OnGoingProject[]>(
      '/spot/api/deposit-activity/v2/project/ongoing/projectList',
      params
    );
  };
  return fetchWithCache(fn, ConstantCacheKeys.ONGOING_PROJECT_LIST);
};

export interface EndProject extends Project {
  rewardAmount: string; // 用户奖励金额(获取的所有奖励)(小数点后4位,末尾抹零)
  prizeType: number; // 奖池模式 1.新用户2.老用户3.新老用户
  prizeToken: string; // 奖池币种
  tokenList: string[]; // 入金&交易配置所有条件币种
}

export const getEndProjectList = (params?: ProjectListParams) => {
  const fn = function () {
    return webapi.get<ProjectList<EndProject>>(
      '/spot/api/deposit-activity/v2/project/end/projectList',
      params
    );
  };
  return fetchWithCache(fn, ConstantCacheKeys.END_PROJECT_LIST);
};

export interface SocialMedia {
  mediaType: number;
  mediaUrl: string;
}

export interface Advertising {
  webIcon: string;
  webUrl: string;
  appIcon: string;
  appUrl: string;
}

export interface ProjectDetail {
  code: string;
  type: number;
  totalPrizePool: string;
  token: string;
  tokenFullName: string;
  icon: string;
  depositStart: number;
  depositEnd: number;
  publishTime: number;
  openTime: number;
  applyStart: number;
  applyEnd: number;
  systemTime: number;
  kycLevel: number;
  userKyc: number;
  isKyb: number;
  newLastUserWinTime: number;
  oldLastUserWinTime: number;
  // isCountryLimit: boolean;
  // 规则
  newUserPrizeTotal: string;
  newUserPrize: string;
  newUserPrizeToken: string;
  oldUserPrizeTotal: string;
  oldUserPrize: string;
  oldUserPrizeToken: string;
  firstDepositNumber: string;
  firstDepositToken: string;
  secondDepositNumber: string;
  secondTradeToken: string;
  supportChain: string;
  remark: string;
  limitCountry: string;
  limitCountryDesc: string;
  isCountryLimit: boolean;
  socialMediaList: SocialMedia[];
  advertisingList: Advertising[];
}

export interface ProjectDetailV2 {
  code: string; // 项目编号
  type: number; // 1.上币前开始活动2上币后开始活动
  totalPrizePool: string; // 总奖池数量（BigDecimal）
  token: string; // 币种简称
  tokenFullName: string; // 币种全名称
  icon: string; // icon路径
  depositStart: number; // 充币开始时间
  depositEnd: number; // 充币结束时间
  publishTime: number; // 公布时间
  openTime: number; // 上币时间
  applyStart: number; // 报名开始时间
  applyEnd: number; // 报名结束时间
  systemTime: number; // 系统时间
  kycLevel: number; // 项目要求kyc等级 1：kyc1,2：kyc2
  userKyc: number; // 用户当前kyc等级, 未登录返回null, 默认值未过KYC ：0, kyc级别一： 1, kyc级别二：2, KYB（无关机构）：3
  isKyb: number; // 用户是否机构用户   true：是 false：否
  newLastUserWinTime: number; // 最后的新的用户中奖时间（时间戳）
  oldLastUserWinTime: number; // 最后的老的用户中奖时间（时间戳）
  // isCountryLimit: boolean;
  // 规则
  newUserPrizeTotal: string; // 新用户奖池金额总量
  newUserPrize: string; // 新用户奖池金额总量（每份）
  newUserPrizeToken: string; // 新用户奖池币种
  oldUserPrizeTotal: string; // 老用户奖池金额总量
  oldUserPrize: string; // 老用户奖池金额总量（每份）
  oldUserPrizeToken: string; // 老用户奖池币种
  firstDepositNumber: string; // 条件一入金数量
  firstDepositToken: string; // 条件一入金币种
  secondDepositNumber: string; // 条件二入金数量
  secondTradeToken: string; // 条件二交易币种
  supportChain: string; // 支持的链(多语言)
  remark: string; // 项目简介(多语言)
  limitCountry: string; // 限制国家
  limitCountryDesc: string; // 限制国家描述
  isCountryLimit: boolean; // 用户在受限制国家  未登录返回false  true 受限制  false 不受限制
  socialMediaList: SocialMedia[]; // 社交媒体列表(多语言)
  advertisingList: Advertising[]; // 广告位列表(多语言)
  coinAssetType: CoinAssetType; // 币种资产类型1新资产2老资产
  prizeType: PrizeType; // 奖池模式 1.新用户2.老用户3.新老用户 （配置为新用户模式时，老用户奖池和入金任务都没有数据，配置为老用户时，新用户奖池和入金任务没有数据，配置为3则都有数据）
  newUserCondition1: TokenAmount[]; // 新用户入金任务1
  newUserCondition2: Condition2DTO; // 新用户入金任务2
  oldUserCondition1: TokenAmount[]; // 老用户入金任务1
  oldUserCondition2: Condition2DTO; // 老用户入金任务2
  totalTradeValue: string; // 总交易额
  taskType: TaskType; // 任务类型 1 充值任务2 交易任务3 充值+交易
  tradeUserPrizeTotal: string; // 交易奖池金额总量
  tradePrizeToken: string; // 交易奖池币种
  tradeStart: number; // 交易开始时间
  tradeEnd: number; // 交易结束时间
  tradeApplyStart: number; // 交易任务报名开始时间
  tradePublishTime: number; // 交易任务结果公布时间
  tradeLimitAmount: string; // 完成任务交易金额
  tradeSymbolList: string; // 交易币对列表，逗号分隔
  tradeToken: string; // 交易币种
  tradeSymbolObjList?: SymbolObj[]; // 交易币对列表,区分币种
  tradeAirdropTop: string; // 交易个人空投硬顶
  prizePool: string; // Referral奖池金额
  awardToken: string; // Referral奖池币种
  stablecoin: string; // stablecoin 动态获取币种，取代原先固定USDT
}

export enum CoinAssetType {
  New = 1,
  Old = 2,
}

export enum PrizeType {
  New = 1,
  Old = 2,
  All = 3,
}

export enum TaskType {
  Deposit = 1,
  Trade = 2,
  All = 3,
}

export interface Condition2DTO {
  depositAmount: string;
  tradeList: TokenAmount[];
}

// 详情
export const getProjectDetailV2 = (projectCode: string) => {
  const fn = function () {
    return webapi.get<ProjectDetailV2>(
      '/spot/api/deposit-activity/v2/project/detail',
      { projectCode }
    );
  };
  return fetchWithCache(
    fn,
    `${ConstantCacheKeys.PROJECT_DETAIL}_${projectCode}`
  );
};

// 参加活动
export const joinProject = (projectCode: string) => {
  return webapi.post('/spot/api/deposit-activity/v1/project/pledge', {
    projectCode,
  });
};

export interface UserProjectParams {
  projectCode: string;
  isFresh: boolean; //默认false 为true时会直接请求（只有用户点击刷新时才请求）
}

export interface UserProjectData {
  isJoin: boolean;
  rewardStatus: number; //0初始（未开奖），1:中奖，2未中奖
  isNewUser: boolean; //是否新用户 1:新用户  2：老用户
  firstDepositAmount: string; //条件一充值金额
  secondDepositAmount: string; //条件二充值金额
  secondIsTrade: boolean; //条件二是否交易 默认false
}

// 自动赚币查询
export const getUserActivity = (params: UserProjectParams) => {
  return webapi.get<UserProjectData>(
    '/spot/api/deposit-activity/v1/project/userProject',
    params
  );
};

export interface TokenAmount {
  token: string;
  amount: string;
}

export enum RewardStatus {
  Init = 0,
  Hit = 1,
  Miss = 2,
  Unjoin = 3,
  Risk = 4,
}

interface notNewUserReasonMap {
  isJoin: boolean;
  isRegisterInTime: boolean;
  joinProject: string;
}
export interface UserProjectDataV2 {
  isJoin: boolean;
  joinType: JoinType; // 报名类型 0未参加 1 充值活动2 交易活动3 充值+交易活动
  rewardStatus: number; //0初始（未开奖），1:中奖，2未中奖
  isNewUser: boolean; //是否新用户 1:新用户  2：老用户
  firstDeposit: TokenAmount[]; //条件一充值金额
  isFirstDone: boolean; //条件一是否完成
  secondDepositAmount: string; //条件二充值金额
  secondTrade: TokenAmount[]; //条件二交易金额
  isSecondDone: boolean; //条件二是否完成
  tradeRewardStatus: RewardStatus; // 0初始（未开奖），1:中奖，2未中奖 3 未参与 4.被风控
  totalTradeValue: string; // 总交易额
  rewardAmount: string; // 预估奖励金额
  tradeAmount: string; // 用户交易金额
  notNewUserReason: notNewUserReasonMap;
}

export enum JoinType {
  Unjoin = 0,
  DepositJoin = 1,
  TradeJoin = 2,
  AllJoin = 3,
}

export interface TokenDetailData {
  iconUrl: string;
  tokenName: string;
  tokenFullName: string;
  publishTime: string;
  maxQuantitySupplied: string;
  officialWebsiteUrl: string;
  whitePaperUrl: string;
  tokenDetail: string;
  iconDayUrl: string;
  marketCap: string;
}

// 用户进度查询
export const getUserActivityV2 = (projectCode: string) => {
  const fn = function () {
    return webapi.get<UserProjectDataV2>(
      '/spot/api/deposit-activity/v2/project/userProject',
      { projectCode }
    );
  };
  return fetchWithCache(fn, `${ConstantCacheKeys.USER_PROJECT}_${projectCode}`);
};

// 获取重复用户
export const getDuplicateUserIds = (projectCode: string) => {
  return webapi.get(
    '/spot/api/deposit-activity/v1/project/getDuplicateUserIds',
    { projectCode }
  );
};

// 获取币种详情
export const getTokenDetail = (tokenId: string) => {
  return webapi.get<TokenDetailData>('/spot/api/token/get/detail', { tokenId });
};

export interface SymbolObj {
  symbolId: string;
  baseTokenId: string;
  quoteTokenId: string;
}
